import { hubPageBg } from './accountHubStyles';
import { FC, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useAsUserParam } from './useAsUserParam';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import CheckIcon from '@mui/icons-material/Check';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import {
  setLoginSecurity,
  setLoginSecurityLoading,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import axios from 'src/utils/axios';

const cardSx = {
  backgroundColor: 'background.paper',
  borderRadius: '16px',
  boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
  p: 2,
};

const TFAPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const asUser = useAsUserParam();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}`;
  const setupRoute = `${backRoute}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA_SETUP}`;
  const manageRoute = `${backRoute}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_TFA_MANAGE}`;
  const backupRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY}/${RENTER_PATHS.ACCOUNT_HUB_LOGIN_SECURITY_BACKUP_CODES}`;

  const loginSecurity = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.loginSecurity
  );

  const fetchStatus = useCallback(async (): Promise<void> => {
    dispatch(setLoginSecurityLoading(true));
    try {
      const resp = await axios.get(`v2/user/totp/enroll/${asUser}`);
      dispatch(
        setLoginSecurity({
          totp_enabled: Boolean(resp.data?.confirmed),
          totp_confirmed_at: resp.data?.confirmed_at ?? null,
          backup_codes_remaining: resp.data?.backup_codes_remaining ?? 0,
          backup_codes_generated_at:
            resp.data?.backup_codes_generated_at ?? null,
        })
      );
    } catch {
      dispatch(setLoginSecurityLoading(false));
    }
  }, [dispatch, asUser]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  if (loginSecurity.loading) {
    return (
      <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
        <PageHeader
          title="Two-Factor Authentication"
          icon={<SecurityIcon />}
          backRoute={backRoute}
          subtitle="Add an extra layer of security"
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Two-Factor Authentication"
        icon={<SecurityIcon />}
        backRoute={backRoute}
        subtitle="Add an extra layer of security"
      />
      <ProxyBanner />

      <Stack gap={2.5} sx={{ p: 3 }}>
        {/* Current Status card — same in both states */}
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5 }}>
            Current Status
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              Two-Factor Authentication
            </Typography>
            <Chip
              label={loginSecurity.totp_enabled ? 'Enabled' : 'Disabled'}
              size="small"
              sx={
                loginSecurity.totp_enabled
                  ? {
                      backgroundColor: 'primary.lighter',
                      color: 'primary.dark',
                      fontWeight: 500,
                      fontSize: 12,
                      borderRadius: '12px',
                      height: 'auto',
                      '& .MuiChip-label': { px: 1, py: 0.5 },
                    }
                  : {
                      backgroundColor: 'error.lighter',
                      color: 'error.dark',
                      fontWeight: 600,
                      fontSize: 12,
                      borderRadius: '4px',
                      height: 'auto',
                      '& .MuiChip-label': { px: 1, py: 0.5 },
                    }
              }
            />
          </Stack>
        </Box>

        {loginSecurity.totp_enabled ? (
          <>
            {/* Authentication Methods card — matches mockup qqvkg */}
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 0.5 }}>
                Authentication Methods
              </Typography>
              <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 2 }}>
                Choose how you want to verify your identity when signing in.
              </Typography>

              {/* Authenticator app inner card */}
              <Box
                sx={{
                  backgroundColor: 'action.hover',
                  borderRadius: '12px',
                  p: 2,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1.5}
                  sx={{ mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '20px',
                      backgroundColor: 'secondary.lighter',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PhoneAndroidIcon
                      sx={{ color: 'secondary.main', fontSize: 20 }}
                    />
                  </Box>
                  <Stack gap={0.25} sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      Authenticator App
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Use Google Authenticator, Authy, or similar
                    </Typography>
                    {loginSecurity.totp_confirmed_at && (
                      <Typography
                        sx={{ fontSize: 12, color: 'text.secondary' }}
                      >
                        Last used:{' '}
                        {new Date(
                          loginSecurity.totp_confirmed_at
                        ).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={() => navigate(manageRoute)}
                  sx={{
                    backgroundColor: 'background.paper',
                    color: 'primary.main',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                    fontSize: 13,
                    fontWeight: 500,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: 'grey.300',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Manage
                </Button>
              </Box>
            </Box>

            {/* Backup Codes card */}
            <Box sx={cardSx}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                Backup Codes
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}
              >
                Generate backup codes to use when you don&apos;t have access to
                your authenticator.
              </Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
                {loginSecurity.backup_codes_remaining} of 10 codes remaining
              </Typography>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate(backupRoute)}
                sx={{
                  borderRadius: '12px',
                  fontWeight: 600,
                  py: 1.5,
                }}
              >
                Regenerate Codes
              </Button>
            </Box>
          </>
        ) : (
          /* Not configured — ZrQXC layout */
          <Box sx={cardSx}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
              Why Enable 2FA?
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: 'text.secondary',
                mb: 1.5,
                lineHeight: 1.6,
              }}
            >
              Two-factor authentication adds an extra layer of security to your
              account. Even if someone gets your password, they won&apos;t be
              able to access your account without the second factor.
            </Typography>
            <Stack gap={1} sx={{ mb: 2.5 }}>
              {[
                'Protect against unauthorized access',
                'Required for accessing sensitive information',
                'Backup codes in case you lose your device',
              ].map((text) => (
                <Stack key={text} direction="row" alignItems="center" gap={1}>
                  <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                  <Typography sx={{ fontSize: 13, color: 'text.primary' }}>
                    {text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(setupRoute)}
              sx={{
                borderRadius: '12px',
                fontWeight: 600,
                py: 1.5,
              }}
            >
              Set Up Two-Factor Authentication
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default TFAPage;
